/**
 * Single source of truth for the "Listed on" footer badge wall.
 *
 * Each entry is a directory we've submitted to and that requires us to
 * link back. Used by:
 *  - Landing page <HomeFooter> (React, client-side) and the LandingV2 footer
 *  - server/_core/seo.ts SSR body inject (crawlers without JS)
 *
 * Add a new entry the moment we submit — the link must be live before
 * the directory's verification bot crawls. Remove if a directory later
 * unlists us or shutters.
 *
 * Order = chronological submission (oldest first). Newest at the end.
 */

export type DirectoryListing = {
  /** Human-readable directory name shown in the badge. */
  name: string;
  /** Their homepage (dofollow target). */
  url: string;
};

export const LISTED_ON_DIRECTORIES: DirectoryListing[] = [
  { name: "SeekTool", url: "https://seektool.ai" },
  { name: "ToolPilot", url: "https://toolpilot.ai" },
  { name: "Beacons", url: "https://beacons.ai/solmiai" },
  { name: "Substack", url: "https://solmiai.substack.com" },
  {
    name: "Press Kit",
    url: "https://gravel-tortellini-967.notion.site/Solmi-Press-Kit-375ce72cde7e8043aaaadba509a58a52",
  },
  { name: "Bluesky", url: "https://bsky.app/profile/solmiai.bsky.social" },
  // Oct 4 2026: free listings that require a crawlable link back
  // (zPlatform: badge for the free tier; dang.ai: backlink-verified free tier).
  { name: "zPlatform", url: "https://zplatform.ai" },
  { name: "Dang.ai", url: "https://dang.ai" },
];

/**
 * Render the "Listed on" section as plain HTML for SSR injection.
 * Returns empty string if no directories yet (so we don't ship an
 * empty container).
 */
export function renderListedOnHtml(): string {
  if (LISTED_ON_DIRECTORIES.length === 0) return "";
  const links = LISTED_ON_DIRECTORIES.map(
    (d) =>
      `<a class="listed-on__item" href="${d.url}" rel="noopener" target="_blank">${escape(d.name)}</a>`,
  ).join("");
  return `<aside class="listed-on" aria-label="Listed on"><span class="listed-on__label">Listed on</span>${links}</aside>`;
}

function escape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
